import { useEffect, useState } from "react";
import { Plus, Pencil, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading, EmptyState } from "@/components/States";
import BulkUpload, { downloadFile } from "@/components/admin/BulkUpload";
import { api, fetchSubjects, formatError, DIFFICULTIES } from "@/lib/api";

const STATUS_COLOR = { approved: "bg-emerald-100 text-emerald-800", pending: "bg-amber-100 text-amber-800", retired: "bg-line text-ink-2" };
const blank = (sid) => ({ subject_id: sid, topic: "", difficulty: "beginner", question: "", options: ["", "", "", ""], correct_index: 0, explanation: "", status: "approved" });

function QuestionDialog({ open, onClose, initial, subjects, onSaved }) {
  const [f, setF] = useState(initial);
  useEffect(() => setF(initial), [initial]);
  if (!f) return null;
  const subject = subjects.find((s) => s.id === f.subject_id);
  const save = async () => {
    try {
      const body = { subject_id: f.subject_id, topic: f.topic, difficulty: f.difficulty, question: f.question, options: f.options, correct_index: Number(f.correct_index), explanation: f.explanation, status: f.status };
      if (f.id) await api.put(`/admin/questions/${f.id}`, body); else await api.post("/admin/questions", body);
      toast.success("Question saved"); onSaved(); onClose();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-surface">
        <DialogHeader><DialogTitle>{f.id ? "Edit question" : "New question"}</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-3" data-testid="question-dialog">
          <div className="grid gap-3 sm:grid-cols-3">
            <Select value={f.subject_id} onValueChange={(v) => setF({ ...f, subject_id: v, topic: "" })}>
              <SelectTrigger data-testid="qd-subject"><SelectValue placeholder="Subject" /></SelectTrigger>
              <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.topic || undefined} onValueChange={(v) => setF({ ...f, topic: v })}>
              <SelectTrigger data-testid="qd-topic"><SelectValue placeholder="Topic" /></SelectTrigger>
              <SelectContent>
                {f.topic && !subject?.topics.some((t) => t.name === f.topic) && <SelectItem value={f.topic}>{f.topic}</SelectItem>}
                {subject?.topics.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={f.difficulty} onValueChange={(v) => setF({ ...f, difficulty: v })}>
              <SelectTrigger data-testid="qd-difficulty" className="capitalize"><SelectValue /></SelectTrigger>
              <SelectContent>{DIFFICULTIES.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Textarea data-testid="qd-question" placeholder="Question text" value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} />
          {f.options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="radio" data-testid={`qd-correct-${i}`} name="correct" checked={Number(f.correct_index) === i} onChange={() => setF({ ...f, correct_index: i })} aria-label={`Mark option ${i + 1} correct`} />
              <Input data-testid={`qd-option-${i}`} placeholder={`Option ${"ABCD"[i]}`} value={o} onChange={(e) => setF({ ...f, options: f.options.map((x, j) => (j === i ? e.target.value : x)) })} />
            </div>
          ))}
          <Textarea data-testid="qd-explanation" placeholder="Explanation" value={f.explanation} onChange={(e) => setF({ ...f, explanation: e.target.value })} />
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger data-testid="qd-status" className="capitalize"><SelectValue /></SelectTrigger>
            <SelectContent>{["approved", "pending", "retired"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="qd-save-btn" onClick={save}>Save question</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminQuestions() {
  const [subjects, setSubjects] = useState([]);
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState({ status: "pending", subject_id: "all" });
  const [editing, setEditing] = useState(null);
  const load = () => {
    const p = new URLSearchParams();
    if (filter.status !== "all") p.set("status", filter.status);
    if (filter.subject_id !== "all") p.set("subject_id", filter.subject_id);
    api.get(`/admin/questions?${p}`).then((r) => setItems(r.data));
  };
  useEffect(() => { fetchSubjects().then(setSubjects); }, []);
  useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/questions/${id}/status`, { status }); toast.success(`Question ${status}`); load(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
          <SelectTrigger data-testid="aq-filter-status" className="w-40 capitalize"><SelectValue /></SelectTrigger>
          <SelectContent>{["all", "pending", "approved", "retired"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filter.subject_id} onValueChange={(v) => setFilter({ ...filter, subject_id: v })}>
          <SelectTrigger data-testid="aq-filter-subject" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All subjects</SelectItem>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button data-testid="aq-export-btn" variant="outline" onClick={() => {
            const p = new URLSearchParams({ status: filter.status });
            if (filter.subject_id !== "all") p.set("subject_id", filter.subject_id);
            downloadFile(`/admin/questions/export?${p}`, `learnai_question_bank_${filter.subject_id !== "all" ? `${filter.subject_id}_` : ""}${filter.status}.xlsx`);
          }}>
            <Download className="mr-1 h-4 w-4" />Export to Excel
          </Button>
          <BulkUpload onDone={() => setFilter({ ...filter, status: "approved" })} />
          <Button data-testid="aq-new-btn" onClick={() => setEditing(blank(subjects[0]?.id || ""))}><Plus className="mr-1 h-4 w-4" />New question</Button>
        </div>
      </div>
      {!items ? <Loading /> : items.length === 0 ? <EmptyState testId="aq-empty" title="No questions match" text="AI-generated quiz questions appear here as 'pending' for review." /> : (
        <div data-testid="aq-list" className="flex flex-col gap-3">
          {items.map((q) => (
            <div key={q.id} data-testid={`aq-item-${q.id}`} className="rounded-xl border border-line bg-surface p-5">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                <span className={`rounded-full px-2 py-0.5 font-medium capitalize ${STATUS_COLOR[q.status]}`}>{q.status}</span>
                <Badge variant="outline">{q.subject_id}</Badge><Badge variant="outline" className="capitalize">{q.difficulty}</Badge>
                <span className="text-ink-3">{q.topic} · source: {q.source}</span>
              </div>
              <p className="font-semibold text-ink">{q.question}</p>
              <ol className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
                {q.options.map((o, i) => <li key={i} className={i === q.correct_index ? "font-semibold text-emerald-700" : "text-ink-2"}>{"ABCD"[i]}. {o}</li>)}
              </ol>
              <p className="mt-2 text-xs text-ink-3">{q.explanation}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {q.status !== "approved" && <Button size="sm" data-testid={`aq-approve-${q.id}`} className="bg-emerald-700 hover:bg-emerald-800" onClick={() => setStatus(q.id, "approved")}>Approve</Button>}
                {q.status !== "retired" && <Button size="sm" variant="outline" data-testid={`aq-retire-${q.id}`} onClick={() => setStatus(q.id, "retired")}>Retire</Button>}
                <Button size="sm" variant="ghost" data-testid={`aq-edit-${q.id}`} onClick={() => setEditing(q)}><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <QuestionDialog open={!!editing} initial={editing} subjects={subjects} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}
